import {
    Box,
    Button,
    Chip,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    FormControl,
    IconButton,
    InputAdornment,
    InputLabel,
    List,
    ListItem,
    ListItemButton,
    MenuItem,
    Paper,
    Select,
    TextField,
    Typography,
    Avatar,
    Collapse,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import AddIcon from '@mui/icons-material/Add';
import LocalOfferIcon from '@mui/icons-material/LocalOffer';
import { queryItemTypes, queryPackingListItems, ItemType } from '../schema';
import { useState, useMemo } from 'react';
import { db } from '../instant';
import { id } from '@instantdb/react';

interface Props {
    open: boolean;
    onClose: () => void;
    eventId: string;
    onItemAdded: () => void;
}

interface QuantityDialogProps {
    open: boolean;
    itemType: ItemType | null;
    packingListQuantity: number;
    onClose: () => void;
    onConfirm: (quantity: number) => void;
}

function QuantityDialog({ open, itemType, packingListQuantity, onClose, onConfirm }: QuantityDialogProps) {
    const [quantityInput, setQuantityInput] = useState('1');

    const minQuantity = -packingListQuantity;
    const quantity = (() => {
        const val = parseInt(quantityInput, 10);
        return Number.isInteger(val) && val >= minQuantity ? val : NaN;
    })();
    const isValid = !Number.isNaN(quantity);

    const handleConfirm = () => {
        if (isValid) {
            onConfirm(quantity);
            setQuantityInput('1');
        }
    };

    const handleClose = () => {
        setQuantityInput('1');
        onClose();
    };

    return (
        <Dialog open={open} onClose={handleClose} maxWidth="xs" fullWidth>
            <DialogTitle>Add to Wish List</DialogTitle>
            <DialogContent>
                <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        {itemType?.name}
                    </Typography>
                    <TextField
                        label="Quantity"
                        type="number"
                        value={quantityInput}
                        onChange={e => setQuantityInput(e.target.value)}
                        inputProps={{ min: minQuantity }}
                        fullWidth
                        autoFocus
                        helperText={packingListQuantity > 0 ? `Min ${minQuantity}` : undefined}
                    />
                </Box>
            </DialogContent>
            <DialogActions>
                <Button onClick={handleClose}>Cancel</Button>
                <Button onClick={handleConfirm} variant="contained" disabled={!isValid}>
                    Add to Wish List
                </Button>
            </DialogActions>
        </Dialog>
    );
}

export default function AddWishListItemDialog({ open, onClose, eventId, onItemAdded }: Props) {
    const { data: itemTypesData } = queryItemTypes();
    const { data: packingData } = queryPackingListItems(eventId);
    const [searchText, setSearchText] = useState('');
    const [categoryFilter, setCategoryFilter] = useState<string>('');
    const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());
    const [quantityDialogOpen, setQuantityDialogOpen] = useState(false);
    const [selectedItemType, setSelectedItemType] = useState<ItemType | null>(null);

    // Get all itemTypes
    const allItemTypes = useMemo(() => (itemTypesData?.itemTypes || []) as ItemType[], [itemTypesData?.itemTypes]);

    // Packing list quantity per item type (for negative wish list bound)
    const packingQuantityByItemType = useMemo(() => {
        const map = new Map<string, number>();
        const packingItems = packingData?.events?.[0]?.packingListItems ?? [];
        for (const item of packingItems as Array<{ itemType?: { id: string } | null; quantity?: number }>) {
            const itemTypeId = item.itemType?.id;
            if (!itemTypeId) continue;
            const qty = item.quantity ?? 0;
            map.set(itemTypeId, (map.get(itemTypeId) ?? 0) + qty);
        }
        return map;
    }, [packingData?.events]);

    // Filter and group items
    const { filteredItems, categories } = useMemo(() => {
        const categoriesSet = new Set<string>();
        const filtered: ItemType[] = [];

        for (const itemType of allItemTypes) {
            // Filter by search text
            if (searchText) {
                const searchLower = searchText.toLowerCase();
                const matchesName = itemType.name?.toLowerCase().includes(searchLower);
                const matchesDescription = itemType.description?.toLowerCase().includes(searchLower);
                if (!matchesName && !matchesDescription) {
                    continue;
                }
            }

            // Filter by category
            if (categoryFilter && itemType.category?.id !== categoryFilter) {
                continue;
            }

            filtered.push(itemType);
            if (itemType.category?.id) {
                categoriesSet.add(itemType.category.id);
            }
        }

        // Sort items by name
        filtered.sort((a, b) => (a.name || '').localeCompare(b.name || ''));

        // Group by category
        const itemsByCategory = new Map<string, ItemType[]>();
        for (const item of filtered) {
            const categoryId = item.category?.id || 'uncategorized';
            if (!itemsByCategory.has(categoryId)) {
                itemsByCategory.set(categoryId, []);
            }
            const bucket = itemsByCategory.get(categoryId);
            if (bucket === undefined) throw new Error('itemsByCategory: missing bucket');
            bucket.push(item);
        }

        // Get unique categories
        const categoryIds: string[] = [];
        for (const id of categoriesSet) categoryIds.push(id);
        const categoriesList = categoryIds
            .map(id => {
                const item = filtered.find(i => i.category?.id === id);
                return item?.category;
            })
            .filter(Boolean)
            .sort((a, b) => (a?.name || '').localeCompare(b?.name || ''));

        return {
            filteredItems: itemsByCategory,
            categories: categoriesList,
        };
    }, [allItemTypes, searchText, categoryFilter]);

    const handleAddClick = (itemType: ItemType) => {
        setSelectedItemType(itemType);
        setQuantityDialogOpen(true);
    };

    const handleQuantityConfirm = async (quantity: number) => {
        if (!selectedItemType) return;

        try {
            // Check if item already exists in wish list for this event
            const { data: existingData } = await db.queryOnce({
                events: {
                    $: {
                        where: {
                            id: eventId,
                        },
                    },
                    wishListItems: {
                        $: {
                            where: {
                                'itemType.id': selectedItemType.id,
                            },
                        },
                    },
                },
            });

            const existingItem = existingData?.events?.[0]?.wishListItems?.[0];

            if (existingItem) {
                // Update existing item quantity
                await db.transact([
                    db.tx.wishListItems[existingItem.id].update({
                        quantity: (existingItem.quantity || 0) + quantity,
                    }),
                ]);
            } else {
                // Create new wish list item
                const wishListItemId = id();
                await db.transact([
                    db.tx.wishListItems[wishListItemId].update({
                        quantity,
                    }),
                    db.tx.wishListItems[wishListItemId].link({ itemType: selectedItemType.id }),
                    db.tx.wishListItems[wishListItemId].link({ event: eventId }),
                ]);
            }

            setQuantityDialogOpen(false);
            setSelectedItemType(null);
            onItemAdded();
        } catch (error) {
            console.error('Error adding wish list item:', error);
            // TODO: Show error toast
        }
    };

    const toggleCategory = (categoryId: string) => {
        setExpandedCategories(prev => {
            const next = new Set(prev);
            if (next.has(categoryId)) {
                next.delete(categoryId);
            } else {
                next.add(categoryId);
            }
            return next;
        });
    };

    const handleClose = () => {
        setSearchText('');
        setCategoryFilter('');
        onClose();
    };

    return (
        <>
            <Dialog open={open} onClose={handleClose} maxWidth="md" fullWidth>
                <DialogTitle>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        <Typography variant="h6">Add Item to Wish List</Typography>
                        <IconButton onClick={handleClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
                        {/* Search Bar */}
                        <TextField
                            placeholder="Search by name or description..."
                            value={searchText}
                            onChange={e => setSearchText(e.target.value)}
                            fullWidth
                            size="small"
                            InputProps={{
                                startAdornment: (
                                    <InputAdornment position="start">
                                        <SearchIcon />
                                    </InputAdornment>
                                ),
                            }}
                        />

                        {/* Category Filter */}
                        <FormControl size="small" fullWidth>
                            <InputLabel>Category</InputLabel>
                            <Select value={categoryFilter} label="Category" onChange={e => setCategoryFilter(e.target.value)}>
                                <MenuItem value="">All Categories</MenuItem>
                                {categories.map(category => (
                                    <MenuItem key={category?.id} value={category?.id || ''}>
                                        {category?.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>

                        {/* Item List */}
                        <Paper sx={{ maxHeight: 400, overflow: 'auto' }}>
                            <List dense sx={{ py: 0 }}>
                                {Array.from(filteredItems.entries()).map(([categoryId, items]) => {
                                    const category = categories.find(c => c?.id === categoryId);
                                    const isExpanded = expandedCategories.has(categoryId);
                                    const categoryColor = category?.color || '#2196F3';

                                    return (
                                        <Box key={categoryId}>
                                            {/* Category Header */}
                                            <ListItemButton
                                                onClick={() => toggleCategory(categoryId)}
                                                sx={{
                                                    bgcolor: 'action.hover',
                                                    borderBottom: '1px solid',
                                                    borderColor: 'divider',
                                                }}
                                            >
                                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                                    {isExpanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                                                    <Chip
                                                        label={category?.name || 'Uncategorized'}
                                                        size="small"
                                                        sx={{
                                                            bgcolor: categoryColor,
                                                            color: 'white',
                                                            fontSize: '0.7rem',
                                                        }}
                                                    />
                                                    <Typography variant="body2" color="text.secondary">
                                                        ({items.length})
                                                    </Typography>
                                                </Box>
                                            </ListItemButton>

                                            {/* Category Items */}
                                            <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                                <List component="div" disablePadding>
                                                    {items.map(item => {
                                                        const packingQty = packingQuantityByItemType.get(item.id) ?? 0;
                                                        return (
                                                            <ListItem
                                                                key={item.id}
                                                                secondaryAction={
                                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            Packing: {packingQty}
                                                                        </Typography>
                                                                        <Button
                                                                            size="small"
                                                                            variant="outlined"
                                                                            startIcon={<AddIcon />}
                                                                            onClick={() => handleAddClick(item)}
                                                                        >
                                                                            Add
                                                                        </Button>
                                                                    </Box>
                                                                }
                                                            >
                                                                <Box
                                                                    sx={{
                                                                        display: 'flex',
                                                                        alignItems: 'center',
                                                                        gap: 2,
                                                                        flex: 1,
                                                                        minWidth: 0,
                                                                    }}
                                                                >
                                                                    <Avatar
                                                                        src={item.photoUrl || undefined}
                                                                        variant="rounded"
                                                                        sx={{
                                                                            width: 40,
                                                                            height: 40,
                                                                            bgcolor: 'grey.200',
                                                                        }}
                                                                    >
                                                                        {!item.photoUrl && <LocalOfferIcon />}
                                                                    </Avatar>
                                                                    <Box sx={{ flex: 1, minWidth: 0 }}>
                                                                        <Typography variant="body1" sx={{ fontWeight: 'medium' }}>
                                                                            {item.name}
                                                                        </Typography>
                                                                        {item.description && (
                                                                            <Typography
                                                                                variant="body2"
                                                                                color="text.secondary"
                                                                                sx={{
                                                                                    overflow: 'hidden',
                                                                                    textOverflow: 'ellipsis',
                                                                                    whiteSpace: 'nowrap',
                                                                                }}
                                                                            >
                                                                                {item.description}
                                                                            </Typography>
                                                                        )}
                                                                    </Box>
                                                                </Box>
                                                            </ListItem>
                                                        );
                                                    })}
                                                </List>
                                            </Collapse>
                                        </Box>
                                    );
                                })}
                            </List>

                            {filteredItems.size === 0 && (
                                <Box sx={{ textAlign: 'center', py: 4 }}>
                                    <Typography variant="body2" color="text.secondary">
                                        No items match search
                                    </Typography>
                                </Box>
                            )}
                        </Paper>
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleClose}>Close</Button>
                </DialogActions>
            </Dialog>

            <QuantityDialog
                open={quantityDialogOpen}
                itemType={selectedItemType}
                packingListQuantity={packingQuantityByItemType.get(selectedItemType?.id ?? '') ?? 0}
                onClose={() => {
                    setQuantityDialogOpen(false);
                    setSelectedItemType(null);
                }}
                onConfirm={handleQuantityConfirm}
            />
        </>
    );
}
